import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, Download, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { resultadosMock } from '../utils/mockData';

const Resultados = () => {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('userToken');
  const [resultados] = useState(resultadosMock);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-[#1e40af] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">
              Acceso restringido
            </h2>
            <p className="text-gray-600 mb-6">
              Para consultar tus resultados debes iniciar sesión en tu cuenta.
            </p>
            <Link to="/login">
              <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white w-full py-6 text-lg">
                Iniciar sesión
              </Button>
            </Link>
            <p className="text-sm text-gray-500 mt-4">
              ¿No tienes cuenta?{' '}
              <Link to="/registro" className="text-[#1e40af] hover:underline font-semibold">
                Regístrate aquí
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Mis Resultados</h1>
          <p className="text-xl opacity-90">Consulta y descarga tus estudios de laboratorio</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Info banner */}
        <div className="bg-blue-50 border-l-4 border-[#1e40af] rounded-lg p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-[#1e40af] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-[#1e3a8a] mb-1">Información importante</h3>
              <p className="text-gray-600 text-sm">
                Tus resultados se mantienen disponibles por 1 año desde la fecha del estudio. 
                Te recomendamos descargarlos y guardarlos para tu registro personal.
              </p>
            </div>
          </div>
        </div>

        {/* Resultados list */}
        {resultados.length > 0 ? (
          <div className="space-y-4">
            {resultados.map((resultado) => (
              <Card key={resultado.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#84cc16] p-3 rounded-lg">
                        <FileText className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#1e3a8a] mb-1">
                          {resultado.estudio}
                        </h3>
                        <p className="text-gray-600 mb-2">
                          Paciente: <span className="font-semibold">{resultado.paciente}</span>
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="h-4 w-4" />
                          <span>Fecha: {new Date(resultado.fecha).toLocaleDateString('es-MX')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <span className="inline-flex items-center justify-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                        {resultado.estado}
                      </span>
                      <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                        <Download className="mr-2 h-4 w-4" />
                        Descargar PDF
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">
                No tienes resultados disponibles
              </h3>
              <p className="text-gray-500 mb-6">
                Aún no hay estudios completados en tu cuenta.
              </p>
              <Link to="/estudios">
                <Button className="bg-[#84cc16] hover:bg-[#65a30d] text-white">
                  Ver estudios disponibles
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Help section */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">¿Necesitas ayuda?</h3>
          <p className="text-gray-600 mb-4">
            Si tienes problemas para acceder a tus resultados o necesitas asistencia para interpretarlos, 
            no dudes en contactarnos.
          </p>
          <Button variant="outline" className="border-[#1e40af] text-[#1e40af] hover:bg-[#1e40af] hover:text-white">
            Contactar soporte
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Resultados;